import { memo, useEffect } from 'react';
import { SpaceBetween, Tabs, TabsProps } from '@amzn/awsui-components-core';

import { AttachmentsReadonlyTabView } from './AttachmentsReadonlyTabView';
import { CodeReadonlyTabView } from './CodeReadonlyTabView';
import { ContentMetadataView } from './ContentMetadataView';
import { ContentsReadonlyTabView } from './ContentsReadonlyTabView';
import { ContentStatusBannersView } from './ContentStatusBannersView';
import { ContentViewTitleView } from './ContentViewTitleView';
import { UpdateRequestsTabView } from './UpdateRequestsTabView';
import { HistoryTabView } from '../v2-content-editor/HistoryTabView';
import { TabId, isTabId, useCurrentTabId, useNavigateToTab } from '../v2-hooks/useUrlHash';
import { usePublishSuccessRedirect } from '../v2-hooks/usePublishSuccessRedirect';
import { cancelPageLoadTimeTracking } from '../../monitoring/PageLoadTimeReporter';

interface LoadedContentViewProps {
  /**
   * Whether to render the status banners (lock, finalizing, bar-raiser notices) above the tabs.
   * Suppressed when the edit page shows this view behind its own lock modal, which already conveys
   * the state — the banners would compete with it. Defaults to true for the standalone viewer.
   */
  readonly showStatusBanners?: boolean;

  /**
   * Whether this view reports the page's load time, which only the view page does. Defaults to
   * false, so the edit page, which shows this view behind its redirect modal, sends nothing.
   */
  readonly isPageLoadTracked?: boolean;
}

/** The read-only content body once the document has loaded: title header plus the tabbed viewing surface. */
export const LoadedContentView = memo(function LoadedContentView({
  showStatusBanners = true,
  isPageLoadTracked = false,
}: LoadedContentViewProps) {
  const currentTabId = useCurrentTabId();
  const navigateToTab = useNavigateToTab();

  // When a bar raiser's publish succeeds, redirect from the draft to the published version.
  usePublishSuccessRedirect();

  const tabs: readonly TabsProps.Tab[] = [
    {
      label: 'Latest version',
      id: 'contents',
      content: <ContentsReadonlyTabView isPageLoadTracked={isPageLoadTracked} />,
    },
    { label: 'Code', id: 'code', content: <CodeReadonlyTabView /> },
    { label: 'Attachments', id: 'attachments', content: <AttachmentsReadonlyTabView /> },
    { label: 'History', id: 'history', content: <HistoryTabView /> },
    { label: 'Update Requests', id: 'update-requests', content: <UpdateRequestsTabView /> },
  ];

  const activeTabId: TabId =
    currentTabId !== undefined && tabs.some((t) => t.id === currentTabId)
      ? currentTabId
      : 'contents';

  // When the tracked page shows any tab other than Contents, either on landing or after a switch,
  // then stop timing, because a later Contents render would also count time spent on that tab.
  useEffect(() => {
    if (!isPageLoadTracked || activeTabId === 'contents') {
      return;
    }

    cancelPageLoadTimeTracking();
  }, [isPageLoadTracked, activeTabId]);

  return (
    <SpaceBetween size="l">
      <ContentViewTitleView />
      {showStatusBanners && <ContentStatusBannersView />}
      <Tabs
        activeTabId={activeTabId}
        onChange={({ detail }) => {
          if (!isTabId(detail.activeTabId)) {
            throw new Error(`Unknown tab id: ${detail.activeTabId}`);
          }
          navigateToTab(detail.activeTabId);
        }}
        tabs={tabs}
      />
      {activeTabId === 'contents' && <ContentMetadataView />}
    </SpaceBetween>
  );
});
